import { API_VERSION } from 'utils/request';
import { createLazyResourceStore, initialResource } from '@leancloud/use-resource';
import { useAPI } from 'utils/use-api';
import { useAppId } from '.';

export type SSLType = 'none' | 'automatic' | 'uploaded';
export type BindingStatus =
  | 'verifyingIcp'
  | 'verifyingCname'
  | 'issuingCert'
  | 'normal'
  | 'suspended'
  | 'failed'
  | 'active'
  | 'inactive';
export type BindingType =
  | 'platform'
  | 'engine'
  | 'engine-cdn'
  | 'engine-preview'
  | 'file'
  | 'billboard';

export interface DomainBinding {
  type: BindingType;
  appId: string;
  domain: string;
  icpLicense: string;
  certPem: string;
  keyPem: string;
  sslType: SSLType;
  sslExpiredAt?: string | null;
  state: BindingStatus;
  cnameTarget?: string;
  dedicatedIPs?: string[];
  createdAt: string;
  updatedAt: string;
  forceHttps: 1 | 0;
  failedReason?: string;
  cnameVerifiedAt?: string | null;
  icpVerifiedAt?: string | null;
  sharedDomain?: boolean;
}

export interface PlatformDomainBinding extends DomainBinding {
  type: 'platform';
}
export interface EngineDomainBinding extends DomainBinding {
  type: 'engine' | 'engine-cdn' | 'engine-preview';
  groupName: string;
}
export interface FileDomainBinding extends DomainBinding {
  type: 'file';
}

export interface BillboardBinding extends DomainBinding {
  type: 'billboard';
}

export function isPlatformDomainBinding(binding: DomainBinding): binding is PlatformDomainBinding {
  return binding.type === 'platform';
}

export function isEngineDomainBinding(binding: DomainBinding): binding is EngineDomainBinding {
  return (
    binding.type === 'engine' || binding.type === 'engine-cdn' || binding.type === 'engine-preview'
  );
}

export function isFileDomainBinding(binding: DomainBinding): binding is FileDomainBinding {
  return binding.type === 'file';
}

export function isBillboardDomainBinding(binding: DomainBinding): binding is BillboardBinding {
  return binding.type === 'billboard';
}

export const isOperational = <T extends DomainBinding>(domain: T) =>
  domain.state === 'normal' || domain.state === 'active';

export interface Group {
  groupName: string;
}

const { useResource: useDomainBindings, Provider } = createLazyResourceStore(
  ({ useTriggered }) =>
    () => {
      const appId = useAppId();

      return useAPI<DomainBinding[]>(`/${API_VERSION}/domain-center/domain-bindings`, { appId }, [
        appId,
      ]);
    },
  initialResource
);
export { useDomainBindings, Provider };
